import {
  isSentinel,
  sentinelLabel,
  rateGuaranteeHasNoCap,
  formatCurrency,
  formatPercent,
  formatMonthsOrYears,
  enumLabel,
  formatServiceWaitingPeriods,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  ELIGIBILITY_HOURS_LABELS,
  EMPLOYER_CONTRIBUTION_LABELS,
  INCLUSION_LABELS,
} from "./shared";

const NOT_INCLUDED = sentinelLabel("notIncluded");

export type DentalPpoFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Extended (non-v5) fields carried on `benefits.extended`. Dropdowns since
 * CD-2325, so the stored value is an enum key; plans quoted before that still
 * hold whatever an analyst typed, which falls through as-is rather than being
 * blanked by a plain `enumLabel` — a blank value makes `fieldValues` drop the
 * row, so legacy data would vanish from the sheet.
 */
const extendedLabel =
  (fieldKey: string, labels: Record<string, string>): DentalPpoFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    if (typeof value !== "string" || value === "") return "";
    if (isSentinel(value)) return sentinelLabel(value);
    return labels[value] ?? value;
  };

// Label tables for the extended dropdowns mirror plan-grid-core's
// dropdownCellSpecs.ts, so the sheet reads the same as the Quotes grid.
const YES_NO_LABELS: Record<string, string> = { yes: "Yes", no: "No" };

// Mirrors plan-grid-core's deductibleWaivedPreventativeLabels, the Yes / No
// the Quotes grid dropdown offers for this field.
const DEDUCTIBLE_WAIVED_PREVENTATIVE_LABELS: Record<string, string> = {
  included: "Yes",
  notIncluded: "No",
};

// notIncluded is a sentinel, so enumLabel would resolve it to "Not Included"
// before consulting the map above; look the map up first instead.
const formatDeductibleWaivedPreventative: DentalPpoFieldFormatter = (row) => {
  const value = row["isDeductibleWaivedPreventative"];
  if (typeof value !== "string" || value === "") return "";
  return (
    DEDUCTIBLE_WAIVED_PREVENTATIVE_LABELS[value] ??
    (isSentinel(value) ? sentinelLabel(value) : "")
  );
};

const TWICE_YEARLY_FREQUENCY_LABELS: Record<string, string> = {
  months6: "1 in 6 months",
  months12: "1 in 12 months",
  twiceIn12Months: "2 in 12 months",
};

const FULL_MOUTH_X_RAYS_FREQUENCY_LABELS: Record<string, string> = {
  months36: "1 per 36 mo.",
  months60: "1 per 60 mo.",
  years5: "1 per 5 years",
};

const MAJOR_SERVICE_LABELS: Record<string, string> = {
  preventative: "Preventative",
  basicIncludingPosterior: "Basic - Including Posterior",
  majorIncludingPosterior: "Major - Including Posterior",
  basicAnteriorOnly: "Basic - Anterior Only",
  majorAnteriorOnly: "Major - Anterior Only",
};

const MAJOR_SERVICE_FREQUENCY_LABELS: Record<string, string> = {
  ...FULL_MOUTH_X_RAYS_FREQUENCY_LABELS,
  years10: "1 per 10 years",
};

const WORK_IN_PROGRESS_INCLUDED_LABELS: Record<string, string> = {
  includedInFull: "Included in Full",
  includedPriorCarrierOnly: "Included – Prior Carrier Only",
  includedLimited: "Included – Limited",
  includedReviewRequired: "Included – Review Required",
  includedPriorPlanRequired: "Included – Prior Plan Required",
};

const OPEN_OR_CLOSED_CONTRACT_LABELS: Record<string, string> = {
  openContract: "Open Contract",
  closedContract: "Closed Contract",
  closedWithExceptions: "Closed – With Exceptions",
};

/** Dollar-amount typed field (individualDeductible, familyDeductible, annualMaximum, orthoMaximum). */
const dollarTyped =
  (typeKey: string, amountKey: string): DentalPpoFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    if (type === "dollarAmountPerPerson")
      return `${formatCurrency(row[amountKey]) || ""} / person`;
    return "";
  };

/** Percentage coinsurance field (preventativeType1, basicType2, majorType3, orthoType4). */
const percentTyped =
  (typeKey: string, amountKey: string): DentalPpoFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "notIncluded") return NOT_INCLUDED;
    if (type === "percentageAmount") return formatPercent(row[amountKey]) || "";
    return "";
  };

const ORTHO_AGE_LIMIT_LABELS: Record<string, string> = {
  adultAndChildren: "Adult & Child(ren)",
  childrenOnlyAgeNotDisclosed: "Child(ren) Only - age not disclosed",
  childrenOnlyToAge: "Children Only to Age",
};

/**
 * Ortho Age Limit — the type and its age are two data points sharing the
 * template's single row, so the age rides on the type's label. Only
 * childrenOnlyToAge carries one; adultAndChildren is age-less in practice,
 * which is what the rule's "if applicable" allows for.
 *
 * The sentinels (Not Included / Not Applicable / Not Offered / Not Disclosed)
 * are the rule's own enum values and resolve through the shared table.
 */
const formatOrthoAgeLimit: DentalPpoFieldFormatter = (row) => {
  const type = row["orthoAgeLimitType"];
  if (typeof type !== "string" || type === "") return "";
  if (isSentinel(type)) return sentinelLabel(type);

  const label = ORTHO_AGE_LIMIT_LABELS[type];
  if (type === "childrenOnlyToAge") {
    const age = row["orthoAgeLimitAge"];
    return age != null ? `${label} ${String(age)}` : label;
  }

  // A type outside the rule's enum reads as Not Included, per its else branch.
  return label ?? NOT_INCLUDED;
};

const SERVICE_CATEGORY_LABELS: Record<string, string> = {
  preventive: "Preventive",
  basic: "Basic",
  major: "Major",
  notIncluded: NOT_INCLUDED,
};

const serviceCategory =
  (fieldKey: string): DentalPpoFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    if (typeof value !== "string" || value === "") return "";
    if (isSentinel(value)) return sentinelLabel(value);
    return SERVICE_CATEGORY_LABELS[value] ?? "";
  };

const OUT_OF_NETWORK_LABELS: Record<string, string> = {
  "45thUcr": "45th UCR",
  "50thUcr": "50th UCR",
  "51stUcr": "51st UCR",
  "70thUcr": "70th UCR",
  "75thUcr": "75th UCR",
  "80thUcr": "80th UCR",
  "85thUcr": "85th UCR",
  "90thUcr": "90th UCR",
  "95thUcr": "95th UCR",
  "99thUcr": "99th UCR",
  mac: "MAC",
  mpa: "MPA",
  feeSchedule: "Fee Schedule",
  notIncluded: NOT_INCLUDED,
};

const DEDUCTIBLE_DURATION_LABELS: Record<string, string> = {
  calendarYear: "Calendar Year",
  lifetime: "Lifetime",
  notIncluded: NOT_INCLUDED,
};

/** Rate Guarantee / Rate Caps — siblings: rateGuaranteeAmount, rateGuaranteeCap. */
const formatRateGuarantee: DentalPpoFieldFormatter = (row) => {
  const type = row["rateGuaranteeType"];
  const cap = row["rateGuaranteeCap"];
  const capText =
    typeof cap === "string" && !rateGuaranteeHasNoCap(type) ? cap.trim() : "";

  if (isSentinel(type)) {
    const label = sentinelLabel(type as string);
    return capText ? `${label}\n${capText}` : label;
  }

  // Deliberately unlike the shared rate guarantee formatter: dental leaves a
  // months/years type with no amount blank instead of printing the bare type label.
  if (row["rateGuaranteeAmount"] == null) return "";
  const line = formatMonthsOrYears(type, row["rateGuaranteeAmount"]);
  if (!line) return "";
  return capText ? `${line}\n${capText}` : line;
};

const formatNetworkName: DentalPpoFieldFormatter = (row) => {
  const value = row["networkName"];
  return typeof value === "string" ? value : "";
};

export const dentalPpoFormatters: Record<string, DentalPpoFieldFormatter> = {
  networkName: formatNetworkName,
  // Network-dependent typed fields
  individualDeductible: dollarTyped(
    "individualDeductibleType",
    "individualDeductibleAmount",
  ),
  familyDeductible: dollarTyped(
    "familyDeductibleType",
    "familyDeductibleAmount",
  ),
  annualMaximum: dollarTyped("annualMaximumType", "annualMaximumAmount"),
  preventativeType1: percentTyped(
    "preventativeType1Type",
    "preventativeType1Amount",
  ),
  basicType2: percentTyped("basicType2Type", "basicType2Amount"),
  majorType3: percentTyped("majorType3Type", "majorType3Amount"),
  orthoType4: percentTyped("orthoType4Type", "orthoType4Amount"),
  orthoMaximum: dollarTyped("orthoMaximumType", "orthoMaximumAmount"),
  orthoAgeLimit: formatOrthoAgeLimit,
  serviceWaitingPeriods: formatServiceWaitingPeriods,

  // Service category fields
  sealants: serviceCategory("sealants"),
  spaceMaintainers: serviceCategory("spaceMaintainers"),
  generalAnesthesia: serviceCategory("generalAnesthesia"),
  compositeWhiteFillings: serviceCategory("compositeWhiteFillings"),
  endodontics: serviceCategory("endodontics"),
  periodonticsNonSurgical: serviceCategory("periodonticsNonSurgical"),
  periodonticsSurgical: serviceCategory("periodonticsSurgical"),
  oralSurgerySimple: serviceCategory("oralSurgerySimple"),
  oralSurgeryComplex: serviceCategory("oralSurgeryComplex"),
  implants: serviceCategory("implants"),

  // Non-network enum fields
  deductibleDuration: enumLabel(
    "deductibleDuration",
    DEDUCTIBLE_DURATION_LABELS,
  ),
  isDeductibleWaivedPreventative: formatDeductibleWaivedPreventative,
  outOfNetworkReimbursement: enumLabel(
    "outOfNetworkReimbursement",
    OUT_OF_NETWORK_LABELS,
  ),
  maximumRollover: enumLabel("maximumRollover", INCLUSION_LABELS),
  missingToothExclusion: enumLabel("missingToothExclusion", INCLUSION_LABELS),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  dependentAgeLimitYears: (row) => {
    const years = row["dependentAgeLimitYears"];
    if (typeof years !== "number") return "";
    return `${years} ${years === 1 ? "year" : "years"}`;
  },
  employerContribution: enumLabel(
    "employerContribution",
    EMPLOYER_CONTRIBUTION_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,

  // Extended (non-v5) fields
  preventiveAdvantage: extendedLabel("preventiveAdvantage", YES_NO_LABELS),
  oralExams: extendedLabel("oralExams", SERVICE_CATEGORY_LABELS),
  oralExamFrequency: extendedLabel(
    "oralExamFrequency",
    TWICE_YEARLY_FREQUENCY_LABELS,
  ),
  cleaningFrequency: extendedLabel(
    "cleaningFrequency",
    TWICE_YEARLY_FREQUENCY_LABELS,
  ),
  bitewingXRays: extendedLabel("bitewingXRays", SERVICE_CATEGORY_LABELS),
  fullMouthXRays: extendedLabel("fullMouthXRays", SERVICE_CATEGORY_LABELS),
  fullMouthXRaysFrequency: extendedLabel(
    "fullMouthXRaysFrequency",
    FULL_MOUTH_X_RAYS_FREQUENCY_LABELS,
  ),
  fluorideTreatments: extendedLabel(
    "fluorideTreatments",
    SERVICE_CATEGORY_LABELS,
  ),
  amalgamSilverFillings: extendedLabel(
    "amalgamSilverFillings",
    SERVICE_CATEGORY_LABELS,
  ),
  simpleExtractions: extendedLabel(
    "simpleExtractions",
    SERVICE_CATEGORY_LABELS,
  ),
  bridgesDentures: extendedLabel("bridgesDentures", MAJOR_SERVICE_LABELS),
  bridgesDenturesFrequency: extendedLabel(
    "bridgesDenturesFrequency",
    MAJOR_SERVICE_FREQUENCY_LABELS,
  ),
  crowns: extendedLabel("crowns", MAJOR_SERVICE_LABELS),
  crownsFrequency: extendedLabel(
    "crownsFrequency",
    MAJOR_SERVICE_FREQUENCY_LABELS,
  ),
  tmj: extendedLabel("tmj", INCLUSION_LABELS),
  workInProgressIncluded: extendedLabel(
    "workInProgressIncluded",
    WORK_IN_PROGRESS_INCLUDED_LABELS,
  ),
  openOrClosedContract: extendedLabel(
    "openOrClosedContract",
    OPEN_OR_CLOSED_CONTRACT_LABELS,
  ),
  eligibilityHours: extendedLabel("eligibilityHours", ELIGIBILITY_HOURS_LABELS),
};

export const formatDentalPpoField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = dentalPpoFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
